<template>
  <div>
    <v-data-table
      v-bind="$attrs"
      :class="{ 'fixed-layout': fixed, 'table-bordered': bordered }"
      :fixed-header="stickyHeader !== 'noSticky'"
      :style="{
        'max-height': stickyHeader !== 'noSticky' ? stickyHeader : undefined,
      }"
      :headers="tableFields"
      :items="tableItems"
      :show-select="isSelectable"
      :item-value="primaryKey"
      :must-sort="true"
      filter-mode="every"
      :filter-keys="filterKeys"
      :search="filterValue"
      v-model:sort-by="sortBy"
      :items-per-page="perPage"
      :hide-no-data="'no-data-text' in $attrs ? false : true"
      :custom-filter="customFilter"
      v-model:page="currentPage"
      @update:options="optionsChanged"
    >
      <template v-slot:loading>
        <v-progress-circular
          indeterminate="disable-shrink"
          color="primary"
          class="ma-1"
        ></v-progress-circular>
      </template>
      <template #top="{ page, itemsPerPage, pageCount }" v-if="perPage !== -1">
        <div class="d-flex justify-space-between align-center mb-2">
          <span v-show="visualCount > 0">
            {{ visualCount }}件中
            {{ itemsPerPage * page - itemsPerPage + 1 }} 〜
            {{
              itemsPerPage * page > visualCount
                ? visualCount
                : itemsPerPage * page
            }}件を表示
          </span>
          <span v-show="visualCount === 0">0件中 0 〜 0件を表示</span>
          <div class="text-center">
            <v-pagination
              v-model="currentPage"
              :length="pageCount"
              show-first-last-page
              total-visible="6"
              first-icon="$mdiChevronDoubleLeft"
              last-icon="$mdiChevronDoubleRight"
              color="primary"
              density="compact"
              rounded="0"
              variant="outlined"
            >
            </v-pagination>
          </div>
        </div>
      </template>
      <!--   HEADER defaultのheaderなら、fieldのalign属性の影響を受けって、タイトルやソートアイコンの表示位置はV2と違うので、ここでカスタム化する  -->
      <template v-slot:headers="{ columns, getSortIcon, toggleSort, isSorted }">
        <tr>
          <template v-for="field in columns" :key="`header${field.key}}`">
            <th
              class="v-data-table__td v-data-table-column--align-center v-data-table__th"
              :class="{
                'v-data-table__th--sortable': field.sortable,
                'v-data-table__th--sorted': field.sortable && isSorted(field),
              }"
              v-on:click="field.sortable ? toggleSort(field) : undefined"
              :style="{
                width:
                  field.key === 'data-table-select'
                    ? '50px'
                    : field.width
                      ? field.width
                      : 'auto',
                'min-width': field.minWidth ? field.minWidth : undefined,
                'max-width': field.maxWidth ? field.maxWidth : undefined,
              }"
            >
              <div class="v-data-table-header__content justify-center">
                <slot
                  :name="`head(${field.key})`"
                  v-bind="{ field, getSortIcon, toggleSort, isSorted }"
                >
                  <span>{{ field.title }}</span>
                  <v-icon
                    v-if="isSortableField(field.key)"
                    class="v-data-table-header__sort-icon"
                    :icon="getSortIcon(field)"
                  />
                </slot>
              </div>
            </th>
          </template>
        </tr>
      </template>
      <!--        BODY-->
      <template v-slot:item="{ item, internalItem, toggleExpand, isExpanded }">
        <v-data-table-row
          :item="internalItem"
          :cell-props="mergeTdClass"
          v-bind="mergeTrClass(item)"
        >
          <!--          selectBoxの表示-->
          <template v-slot:[`item.data-table-select`]="data">
            <template v-if="!!$slots[`cell(_selected)`]">
              <slot name="cell(_selected)" v-bind="data" />
            </template>
            <template v-else>
              <slot name="cell(_selected)" v-bind="data">
                <v-checkbox-btn
                  :value="data.item"
                  v-model="tableSelected"
                  @click.stop
                ></v-checkbox-btn>
              </slot>
            </template>
          </template>
          <template
            v-for="field in tableFields"
            :key="field.key"
            v-slot:[`item.${field.key}`]="{ internalItem }"
          >
            <slot
              :name="`cell(${field.key})`"
              :value="
                formatterMap[field.key]
                  ? formatterMap[field.key].formatter(
                      item[field.key],
                      field.key,
                      item
                    )
                  : item[field.key]
              "
              :item="item"
              :index="internalItem.index"
              :internalItem="internalItem"
              :toggleExpand="toggleExpand"
              :isExpanded="isExpanded"
            >
              <template v-if="formatterMap[field.key]">
                {{
                  formatterMap[field.key].formatter(
                    item[field.key],
                    field.key,
                    item
                  )
                }}
              </template>
              <template v-else>{{ item[field.key] }}</template>
            </slot>
          </template>
        </v-data-table-row>
      </template>
      <template v-slot:expanded-row="{ item, columns }">
        <tr>
          <td :colspan="columns.length">
            <slot name="expanded-row" :item="item" :columns="columns"> </slot>
          </td>
        </tr>
      </template>
      <!--      FOOTER(because Property hide-default-footer not working at vuetify@3.5.14)-->
      <template v-slot:bottom></template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { SortCompareFunc } from "@/services/SortService";
import get from "lodash/get";

/**
 * フィルターリング処理時のオプション
 * ・like - 部分一致
 * ・equal - 完全一致
 * ・gt - 以上（日付の値にのみ使用）
 * ・lt - 以下（日付の値にのみ使用）
 * ・(item: T, value: unknown) => boolean - カスタムマッチング
 */
type FilterType =
  | "like"
  | "equal"
  | "gt"
  | "lt"
  | ((item: unknown, value: unknown) => boolean);
export type FilterOption = Record<string, FilterType>;

/**
 * VuetifyのTYPEはExportされてないので自分で用意
 *  https://github.com/vuetifyjs/vuetify/issues/16680
 * AppTableHeader
 *   key              : 一覧項目のkey名
 *   title            : 一覧のタイトル名
 *   align            : 項目の整列方法、指定しないとはcenter
 *   width,minWidth,maxWidth   : 列幅
 *   sortable         : ソート可否
 *   sortRaw          : ソート処理のCallBackFunction
 *   sortByFormatted  : trueの場合、formated値でソート
 *   filterByFormatted: trueの場合、formated値でフィルタリング
 *   formatter        : フォーマットのCallBackFunction
 */
export type AppTableHeader<T> = {
  readonly key: string;
  readonly title: string;
  readonly align?: "center" | "end" | "start";
  readonly width?: string | number;
  readonly minWidth?: string | number;
  readonly maxWidth?: string | number;
  readonly sortable?: boolean;
  sortRaw?: SortCompareFunc<T>;
  readonly sortByFormatted?: boolean;
  readonly filterByFormatted?: boolean;
  formatter?: (value: any, key: string, item: T) => any;
};

export type SortItem = {
  key: string;
  order: "asc" | "desc";
};

/** itemの型 */
export type TableItem = Record<string, unknown>;

export default defineComponent({
  name: "AppTable",
  data() {
    return {
      /** filter済のItems */
      filteredItemsMap: {} as Record<string, unknown[]>,
      /** 表示されているのページ */
      currentPage: 1,
      /** 選択済みitems */
      tableSelected: this.modelValue,
      /** 一覧のソート項目の保存用 */
      currentSortBy: this.tableSortBy ? [this.tableSortBy] : [],
    };
  },
  props: {
    tableSortBy: {
      type: Object as PropType<SortItem>,
      default: undefined,
      required: false,
    },
    // カラム定義
    fields: {
      type: Array as PropType<Array<AppTableHeader<any>>>,
      required: true,
    },
    // データ配列
    items: {
      type: Array as PropType<Array<any>>,
      default: undefined,
      required: true,
    },
    /** true: 選択可能, false: 選択不可 */
    selectable: {
      type: Boolean as PropType<boolean>,
      default: false,
      required: false,
    },
    /**
     * 主キー相当のプロパティ名.
     * 選択チェックボックスの管理などで使用されているので必須
     * プロパティ名が正しくない場合はwarningが出力される
     * 最悪index相当でも良いがその場合は上記の選択チェックボックスの管理は正しく動かないので注意
     */
    primaryKey: {
      type: String as PropType<string>,
      required: true,
    },
    /**
     * カスタムソート関数
     */
    sortCompare: Function as PropType<SortCompareFunc<any>>,
    /**
     * sortCompareはどの項目に適用することを指定
     * 指定しない場合は全項目適用
     */
    sortCompareKey: {
      type: Array as PropType<Array<string>>,
      required: false,
      default: () => [],
    },
    /** フィルター条件 */
    filter: {
      type: Object as PropType<
        | Record<string, undefined | unknown | { toString: () => string }>
        | undefined
      >,
      default: undefined,
      required: false,
    },
    /** フィルター処理時の挙動オプション */
    filterOption: {
      type: Object as PropType<FilterOption | undefined>,
      default: undefined,
      required: false,
    },
    /** 選択済みItem一覧 */
    modelValue: {
      type: Array as PropType<TableItem[]>,
      default: () => [],
      required: false,
    },
    /**
     * false: null, undefined or empty string values first (less than any other values)
     *  true: null, undefined or empty string values last (greater than any other value)
     */
    sortNullLast: {
      type: Boolean as PropType<boolean>,
      default: false,
      required: false,
    },
    /** 1ページあたりの表示件数、-1の場合はページングしない */
    perPage: {
      type: Number as PropType<number>,
      default: 50,
      required: false,
    },
    /** テーブル高さ
     * 値は`noSticky`の場合はHeader固定を解除
     */
    stickyHeader: {
      type: String as PropType<string>,
      default: "300px",
    },
    /** tdクラス設定のCallBack */
    tdClass: {
      type: Function as PropType<
        (field: AppTableHeader<unknown>) => string[] | undefined
      >,
      required: false,
    },
    /** trクラス設定のCallBack */
    trClass: {
      type: Function as PropType<(item: any) => string[] | undefined>,
      required: false,
    },
    /** table-layout:fixを適用か */
    fixed: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /** table-layout:Borderを適用か */
    bordered: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  mounted() {
    // primary keyが正しくない可能性がある選択状態管理が正しく動かないので要修正
    if (this.tableItems.length !== this.primaryKeySet.size) {
      console.warn(
        `table length !== primary key size. primaryKey: ${this.primaryKey}, table length: ${this.tableItems.length}, ${this.primaryKeySet.size}`,
        this.tableItems
      );
    }
  },
  computed: {
    /**
     * 表示可能のItem件数
     */
    visualCount(): number {
      if (this.filterValue) {
        const filteredItems = Object.entries(this.filteredItemsMap);
        if (filteredItems.length === 0) {
          // 表示可能なItemが0件の場合
          return 0;
        } else if (filteredItems.length === 1) {
          // 表示可能なItemが0件以上の場合
          const value = filteredItems[0][1];
          return value.length;
        }
        console.warn(
          "filteredItemsMap saved more than one filterValue",
          this.filteredItemsMap
        );
        return 0;
      } else {
        // filter値クリアされた場合
        return this.tableItems.length;
      }
    },
    tableFields(): Pick<
      AppTableHeader<TableItem>,
      "key" | "title" | "align" | "sortable" | "sortRaw" | "width"
    >[] {
      return this.fields.map((v) => {
        const { key } = v;
        // 下記の順番でソートメソッドを適用される
        // field.sortRaw > property.sortCompare > 標準ソート
        let compareFunc = undefined;
        if (
          v.sortable &&
          this.sortCompare &&
          (this.sortCompareKey.length === 0 ||
            this.sortCompareKey.includes(key))
        ) {
          // カスタムソートメソッド指定済、かつ該当項目がカスタムソート対象の場合
          compareFunc = this.sortCompare;
        } else if (v.sortable) {
          // v-data-tableの内部ソートはnull-lastの制御はできないから
          // カスタムソート以外は全てここでソート
          compareFunc = (a: TableItem, b: TableItem): number => {
            if (this.sortBy) {
              let aVal, bVal;
              if (v.sortByFormatted) {
                aVal = this.formatterMap[this.sortBy[0].key].formatter(
                  a[this.sortBy[0].key],
                  this.sortBy[0].key,
                  a
                );
                bVal = this.formatterMap[this.sortBy[0].key].formatter(
                  b[this.sortBy[0].key],
                  this.sortBy[0].key,
                  b
                );
              } else {
                aVal = a[this.sortBy[0].key];
                bVal = b[this.sortBy[0].key];
              }
              if (
                (aVal === null || aVal === undefined || aVal === "") &&
                bVal !== null &&
                bVal !== undefined &&
                bVal !== ""
              ) {
                // aがnullまたはundefinedでbはnullまたはundefinedではない場合
                return this.sortNullLast ? 1 : -1;
              } else if (
                (bVal === null || bVal === undefined || bVal === "") &&
                aVal !== null &&
                aVal !== undefined &&
                aVal !== ""
              ) {
                return this.sortNullLast ? -1 : 1;
              } else {
                return `${aVal}`.localeCompare(`${bVal}`, undefined, {
                  numeric: true,
                });
              }
            }
            console.warn(
              `this.sortBy is undefined. table can not start sort process!`
            );
            return 0;
          };
        }
        return {
          key: key,
          title: v.title,
          align: v.align ?? "start",
          width: v.width,
          minWidth: v.minWidth,
          maxWidth: v.maxWidth,
          sortable: v.sortable ? v.sortable : false,
          sortRaw: v.sortRaw ? v.sortRaw : compareFunc,
        };
      });
    },
    filterKeys(): string | string[] | undefined {
      if (this.filterOption) {
        // vuetify内部はItem行数 * filterKey個数としてFilterLoopを実施する
        // AppTableは行単位でFilterするので、v-data-tableへ渡すのfilterKeyは一つで結構
        return Object.keys(this.filterOption)[0];
      }
      return undefined;
    },
    filterValue(): string | undefined {
      if (this.filter) {
        return Object.entries(this.filter).toString();
      }
      return undefined;
    },
    /** table表示のitems */
    tableItems(): TableItem[] {
      return this.items ? this.items : [];
    },
    /** 主キー一覧 */
    primaryKeySet(): Set<unknown> {
      return new Set(this.tableItems.map((e) => get(e, this.primaryKey)));
    },
    /** 選択可能かを取得 */
    isSelectable(): boolean {
      return this.selectable;
    },
    sortBy: {
      get(): SortItem[] | undefined {
        return this.currentSortBy;
      },
      set(value: SortItem[]): void {
        this.currentSortBy = value;
        // ソート項目を画面へ渡す
        this.$emit("update:tableSortBy", value[0]);
      },
    },
    formatterMap(): Record<string, { key: string; formatter: Function }> {
      return (
        this.fields.flatMap((e) => {
          if (typeof e === "object") {
            if (typeof e.formatter === "function") {
              return [e];
            }
            return [];
          }
        }) as { key: string; formatter: Function }[]
      ).toMap((e) => e.key);
    },
  },
  watch: {
    /** 親から選択済みitemの変更をcheckbox側に反映 */
    modelValue() {
      this.tableSelected = this.modelValue;
    },
    /** checkboxの変更を親の選択済みitemに通知 */
    tableSelected(newValue: unknown[]) {
      this.$emit("update:modelValue", newValue);
    },
    items(newValue: TableItem[]) {
      // items更新する際に、filteredItemsMapもクリアする
      // customFilter再適用するため
      this.filteredItemsMap = {};
      // primary keyが正しくない可能性がある選択状態管理が正しく動かないので要修正
      if (this.tableItems.length !== this.primaryKeySet.size) {
        console.warn(
          `table length !== primary key size. primaryKey: ${this.primaryKey}, table length: ${this.tableItems.length}, ${this.primaryKeySet.size}`,
          this.tableItems
        );
      }

      if (!(newValue && this.selectable)) {
        // 空または選択モードじゃない場合は処理不要
        return;
      }
      // 選択済みitemsを最新の状態に更新
      this.tableSelected = this.tableItems.filter((item) =>
        this.tableSelected.some(
          (selected) =>
            get(item, this.primaryKey) === get(selected, this.primaryKey)
        )
      );
    },
    /**
     * 検索後にcurrentは1になることが必要(v2と同じ挙動)
     */
    filter() {
      this.currentPage = 1;
    },
  },
  methods: {
    /**
     * td Classの生成
     */
    mergeTdClass({
      column: field,
    }: {
      column: AppTableHeader<unknown>;
    }): { class: string } | undefined {
      const classes = [];
      if (this.tdClass) {
        const customClasses = this.tdClass(field);
        if (customClasses && customClasses.length > 0) {
          classes.push(...customClasses);
        }
      }
      if (classes.length > 0) {
        return { class: classes.join(" ") };
      }
      return undefined;
    },
    /**
     *  tr Classの生成
     * 「clickable」がある行はクリック可能
     * 「gray-out」がある行は灰色
     */
    mergeTrClass(
      item: TableItem
    ): Record<string, string | Function> | undefined {
      const classes = [];
      if (this.trClass) {
        const customClasses = this.trClass(item);
        if (customClasses && customClasses.length > 0) {
          classes.push(...customClasses);
        }
      } else {
        // デフォルトはclickable
        classes.push("clickable");
      }
      if (classes.length > 0) {
        // クリックできる行のみ、イベントを追加
        if (classes.includes("clickable")) {
          return {
            class: classes.filter((v) => v !== "clickable").join(" "),
            onClick: () => {
              this.$emit("click:row", item);
            },
          };
        } else {
          return {
            class: classes.join(" "),
          };
        }
      }
      return undefined;
    },
    /**
     * フィルタ挙動のCallBack
     * @param _value 使わない
     * @param _query 使わない
     * @param item データ行
     */
    customFilter(_value: string, _query: string, item: any): boolean {
      if (this.filterOption && this.filter) {
        for (const [key, type] of Object.entries(this.filterOption)) {
          // 入力値が空の場合、フィルタ行わない
          const filterValue = this.filter[key];
          if (filterValue === undefined || filterValue === "") {
            continue;
          }
          // フォーマットする
          const filterByFormatted = this.fields.find(
            (field) => field.key === key
          )?.filterByFormatted;
          const formattedValue =
            this.formatterMap[key] && filterByFormatted
              ? this.formatterMap[key].formatter(item.raw[key], key, item.raw)
              : item.raw[key];
          // フィルタ行う
          if (!filter(type, formattedValue, filterValue, item.raw)) {
            return false;
          }
        }
        this.saveFilteredItem(item);
        return true;
      } else {
        // filterできる項目はない場合、true固定
        this.saveFilteredItem(item);
        return true;
      }
    },
    /**
     * filterより表示可能のItemを保存
     * searchValueをキーとする
     * 保存するイメージ:
     *  {searchValue: item[]}
     */
    saveFilteredItem(item: unknown) {
      const key = this.filterValue ? this.filterValue : "";
      const items = this.filteredItemsMap[key];
      if (items) {
        this.filteredItemsMap[key].push(item);
      } else {
        this.filteredItemsMap = { [key]: [item] };
      }
    },
    /**
     * フィルタリング値がクリアされた時或いはfilterValueより表示データがない場合、filteredItemsMapもクリア
     * 1.クリアする場合、customFilterトリガーされてないので、ここでクリア
     * 2.filterValueより表示データがない場合、filteredItemsMapが前回の内容をそのまま残ってるので、ここでクリア
     */
    optionsChanged() {
      if (!this.filterValue || !this.filteredItemsMap[this.filterValue]) {
        this.filteredItemsMap = {};
      }
    },
    /**
     * Fieldがソート可能かの判定
     * @param key Fieldの物理key
     * @return boolean true: ソート可能、false: ソート不可
     * */
    isSortableField(key: string | null): boolean {
      return this.tableFields
        .filter((v) => v.sortable)
        .some((v) => v.key === key);
    },
  },
});

/**
 * true: 検索条件に一致, false: 不一致
 * @param type 検索条件
 * @param targetValue 検索対象値
 * @param filterValue 検索値
 * @param item 検索対象行
 */
function filter(
  type: FilterType,
  targetValue: unknown,
  filterValue: unknown,
  item: unknown
): boolean {
  // カスタム検索
  if (typeof type === "function") {
    return type(item, filterValue);
  }
  // 同じ項目に複数の値が指定された場合、"or"と扱う
  if (Array.isArray(filterValue)) {
    return filterValue.some((e) => filter(type, targetValue, e, item));
  }

  if (
    targetValue === undefined ||
    targetValue === null ||
    `${targetValue}` === ""
  ) {
    if (filterValue === "-") {
      return true;
    } else {
      return targetValue === filterValue;
    }
  } else if (typeof targetValue === "string") {
    switch (type) {
      case "equal":
        return `${targetValue}` === `${filterValue}`;
      case "like":
        return `${targetValue}`.includesIgnoreCase(`${filterValue}`);
      default:
        console.warn(
          "value is string type. filter supported equal/link/custom only",
          targetValue,
          type
        );
        return false;
    }
  } else if (typeof targetValue === "number") {
    switch (type) {
      case "equal":
        return `${targetValue}` === `${filterValue}`;
      case "like":
        return `${targetValue}`.includes(`${filterValue}`);
      default:
        console.warn(
          "value is number type. filter supported equal/link/custom only",
          targetValue,
          type
        );
        return false;
    }
  } else if (typeof targetValue === "boolean") {
    switch (type) {
      case "equal":
        return targetValue === filterValue;
      default:
        console.warn(
          "value is boolean type. filter supported equal/custom only",
          targetValue,
          type
        );
        return false;
    }
  } else if (Array.isArray(targetValue)) {
    return targetValue.some((e) => filter(type, e, filterValue, item));
  } else if (typeof targetValue === "object") {
    switch (type) {
      case "equal":
        return targetValue === filterValue;
      default:
        console.warn(
          "value is object type. filter supported equal/custom only",
          targetValue,
          type
        );
        return false;
    }
  } else {
    console.warn(" value is unknown type", targetValue);
    return targetValue === filterValue;
  }
}
</script>

<style lang="scss" scoped>
// デフォルトの線形ProgressBarは非表示にする
:deep(tr.v-data-table-progress) {
  display: none;
}
:deep(.v-pagination__item) {
  .v-btn {
    width: unset;
    min-width: calc(var(--v-btn-height) + -8px);
  }
}
</style>
